import React, { useState, useEffect, useCallback } from "react";
import { useCookies } from "react-cookie";
import { authenticatedFetch } from "../../utils/api";
import style from "./style.module.css";
import { API_BASE, resolveImageUrl } from "../../config/api";
import { parseApiError } from "../../utils/parseApiError";
import useSnackbar from "../../context/Snackbar/useSnackbar";

interface User {
  user_id: number;
  user_first_name: string;
  user_last_name: string;
  address_id: number;
  store_id: number | null;
  mobile_number: string;
  email: string;
  password: string;
  profile_image: string;
  user_type: string;
  created_at: string;
  updated_at: string;
}

interface Address {
  address_id: number;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
}

interface Store {
  store_id: number;
  store_name: string;
}

interface NewStaff {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  mobile: string;
  user_type: string;
  store_id: string;
  new_store_name: string;
  new_store_description: string;
  new_store_city: string;
  address_name: string;
  address1: string;
  address2: string;
  city: string;
  country: string;
}

const EMPTY_STAFF: NewStaff = {
  first_name: "",
  last_name: "",
  email: "",
  password: "",
  mobile: "",
  user_type: "S",
  store_id: "",
  new_store_name: "",
  new_store_description: "",
  new_store_city: "",
  address_name: "",
  address1: "",
  address2: "",
  city: "",
  country: "",
};

const UserPage = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<NewStaff>(EMPTY_STAFF);
  const [formError, setFormError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [storeImageFile, setStoreImageFile] = useState<File | null>(null);
  const [cookies] = useCookies();
  const snack = useSnackbar();

  const userType: string = cookies.auth?.user_type || "";
  const canCreateStaff = userType === "O" || userType === "A";
  // Admin can only ever create Staff; Owner picks the type via the dropdown.
  const effectiveUserType = userType === "A" ? "S" : form.user_type;

  const fetchUsers = useCallback(async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/user/users?page=1&limit=100`, {
        method: "GET",
      }, cookies.auth);

      if (response.ok) {
        const data = await response.json();
        if (data.success && Array.isArray(data.users)) {
          setUsers(data.users);
        } else if (Array.isArray(data)) {
          setUsers(data);
        } else {
          setUsers([]);
        }
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, [cookies.auth]);

  const fetchAddresses = useCallback(async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/address/addresses?page=1&limit=100`, {
        method: "GET",
      }, cookies.auth);

      if (response.ok) {
        const data = await response.json();
        setAddresses(Array.isArray(data?.data) ? data.data : []);
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
    }
  }, [cookies.auth]);

  const fetchStores = useCallback(async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/store?page=1&limit=100`, {
        method: "GET",
      }, cookies.auth);

      if (response.ok) {
        const data = await response.json();
        setStores(Array.isArray(data?.data) ? data.data : []);
      }
    } catch (error) {
      console.error("Error fetching stores:", error);
    }
  }, [cookies.auth]);

  useEffect(() => {
    fetchUsers();
    fetchAddresses();
    fetchStores();
  }, [fetchUsers, fetchAddresses, fetchStores]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const filteredUsers = users.filter(
    (user) =>
      (user.user_first_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.user_last_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.mobile_number || "").includes(searchTerm) ||
      (user.user_type || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getAddressText = (addressId: number) => {
    const address = addresses.find((addr) => addr.address_id === addressId);
    if (!address) return "No Address";
    const line1 = (address as any).address_line1 || (address as any).address1 || "";
    return `${line1}${address.city ? `, ${address.city}` : ""}`.trim() || "No Address";
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Mobile is stored without the +92 prefix — digits only, exactly 10 after +92.
  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = e.target.value.replace(/[^0-9]/g, "").slice(0, 10);
    setForm((prev) => ({ ...prev, mobile: digitsOnly }));
  };

  const handleStoreImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setStoreImageFile(e.target.files[0]);
    }
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(",")[1]);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleCreateStaff = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");

    if (!form.first_name || !form.email || !form.password) {
      setFormError("First name, email and password are required.");
      return;
    }

    if (form.mobile && !/^[0-9]{10}$/.test(form.mobile)) {
      setFormError("Mobile number must be numbers only, exactly 10 digits after +92.");
      return;
    }

    const hasUpperCase = /[A-Z]/.test(form.password);
    const hasLowerCase = /[a-z]/.test(form.password);
    const hasNumber = /[0-9]/.test(form.password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(form.password);
    if (form.password.length < 8) {
      setFormError("Password must be at least 8 characters.");
      return;
    } else if (!hasUpperCase) {
      setFormError("Password must include at least one uppercase letter.");
      return;
    } else if (!hasLowerCase) {
      setFormError("Password must include at least one lowercase letter.");
      return;
    } else if (!hasNumber) {
      setFormError("Password must include at least one number.");
      return;
    } else if (!hasSpecialChar) {
      setFormError("Password must include at least one special character.");
      return;
    }

    if (effectiveUserType === "S" && !form.store_id) {
      setFormError("Please select a store for this Staff user.");
      return;
    }

    setIsLoading(true);
    try {
      const endpoint = userType === "A" ? `${API_BASE}/user/staff` : `${API_BASE}/user`;
      const {
        address_name, address1, address2, city, country,
        mobile, store_id, new_store_name, new_store_description, new_store_city,
        ...rest
      } = form;
      const payload: Record<string, unknown> = { ...rest };
      if (mobile) payload.mobile = `+92${mobile}`;
      if (userType === "O" && !payload.user_type) payload.user_type = "S";
      if (effectiveUserType === "S" && store_id) payload.store_id = store_id;
      if (effectiveUserType === "A" && new_store_name.trim() && new_store_description.trim()) {
        const storeImageBase64 = storeImageFile ? await convertFileToBase64(storeImageFile) : undefined;
        payload.store = {
          store_name: new_store_name.trim(),
          store_description: new_store_description.trim(),
          city: new_store_city.trim() || undefined,
          store_image: storeImageBase64,
        };
      }
      if (address_name.trim() && address1.trim() && city.trim() && country.trim()) {
        payload.address = { address_name, address1, address2, city, country };
      }

      const response = await authenticatedFetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }, cookies.auth);

      if (response.ok) {
        setShowModal(false);
        setForm(EMPTY_STAFF);
        setStoreImageFile(null);
        fetchUsers();
        if (userType === "O") fetchStores();
        snack.onResponse({ message: "User created successfully!", status: 200 });
      } else {
        setFormError(await parseApiError(response, "Failed to create user."));
      }
    } catch (err) {
      setFormError("Network error.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={style.wrapper}>
      <div className={style.header}>
        <h1 className={style.title}>
          {userType === "A" ? "My Staff" : "Users"}
        </h1>
        <div className={style.searchSection}>
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={handleSearch}
            className={style.searchInput}
          />
          {canCreateStaff && (
            <button
              className={style.addButton}
              onClick={() => { setShowModal(true); setFormError(""); setForm(EMPTY_STAFF); setShowPassword(false); setStoreImageFile(null); }}
            >
              + Add Staff
            </button>
          )}
        </div>
      </div>

      <div className={style.tableContainer}>
        <table className={style.table}>
          <thead>
            <tr>
              <th>ID</th>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Address</th>
              <th>Mobile</th>
              <th>Email</th>
              <th>Image</th>
              <th>User Type</th>
              <th>Created At</th>
              <th>Updated At</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ textAlign: "center", padding: "20px", color: "#666" }}>
                  {users.length === 0 ? "No users found." : "No users match your search criteria."}
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={user.user_id}>
                  <td>{user.user_id}</td>
                  <td>{user.user_first_name}</td>
                  <td>{user.user_last_name}</td>
                  <td>{getAddressText(user.address_id)}</td>
                  <td>{user.mobile_number}</td>
                  <td>{user.email}</td>
                  <td>
                    <img
                      src={
                        user.profile_image
                          ? resolveImageUrl(user.profile_image)
                          : "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjVGNUY1Ii8+CjxjaXJjbGUgY3g9IjMwIiBjeT0iMjAiIHI9IjgiIGZpbGw9IiNEOUQ5RDkiLz4KPHBhdGggZD0iTTEwIDQ1QzEwIDM3LjI2ODcgMTYuMjY4NyAzMSAyNCAzMUgzNkM0My43MzEzIDMxIDUwIDM3LjI2ODcgNTAgNDVWNTBIMTBWNDVaIiBmaWxsPSIjRDlEOUQ5Ii8+Cjwvc3ZnPgo="
                      }
                      alt="User"
                      className={style.userImage}
                      onError={(e) => {
                        e.currentTarget.src =
                          "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjVGNUY1Ii8+CjxjaXJjbGUgY3g9IjMwIiBjeT0iMjAiIHI9IjgiIGZpbGw9IiNEOUQ5RDkiLz4KPHBhdGggZD0iTTEwIDQ1QzEwIDM3LjI2ODcgMTYuMjY4NyAzMSAyNCAzMUgzNkM0My43MzEzIDMxIDUwIDM3LjI2ODcgNTAgNDVWNTBIMTBWNDVaIiBmaWxsPSIjRDlEOUQ5Ii8+Cjwvc3ZnPgo=";
                      }}
                    />
                  </td>
                  <td>
                    <span className={`${style.userType} ${style[user.user_type]}`}>
                      {user.user_type}
                    </span>
                  </td>
                  <td>{new Date(user.created_at).toLocaleDateString()}</td>
                  <td>{new Date(user.updated_at).toLocaleDateString()}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className={style.modal}>
          <div className={style.modalContent}>
            <h2>Add Staff</h2>
            <form onSubmit={handleCreateStaff} autoComplete="off">
              <div className={style.formRow}>
                <div className={style.formGroup}>
                  <label>First Name *</label>
                  <input name="first_name" value={form.first_name} onChange={handleFormChange} autoComplete="off" required />
                </div>
                <div className={style.formGroup}>
                  <label>Last Name</label>
                  <input name="last_name" value={form.last_name} onChange={handleFormChange} autoComplete="off" />
                </div>
                <div className={style.formGroup}>
                  <label>Email *</label>
                  <input name="email" type="email" value={form.email} onChange={handleFormChange} autoComplete="off" required />
                </div>
                <div className={style.formGroup}>
                  <label>Password *</label>
                  <div className={style.passwordContainer}>
                    <input
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={form.password}
                      onChange={handleFormChange}
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      className={style.eyeButton}
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <p className={style.passwordHint}>Min 8 characters, with uppercase, lowercase, number &amp; special character.</p>
                </div>
                <div className={style.formGroup}>
                  <label>Mobile</label>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span>+92</span>
                    <input
                      name="mobile"
                      value={form.mobile}
                      onChange={handleMobileChange}
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="3001234567"
                    />
                  </div>
                </div>
                {userType === "O" && (
                  <div className={style.formGroup}>
                    <label>User Type</label>
                    <select name="user_type" value={form.user_type} onChange={handleFormChange}>
                      <option value="S">Staff (S)</option>
                      <option value="A">Admin (A)</option>
                    </select>
                  </div>
                )}
                {effectiveUserType === "S" && (
                  <div className={style.formGroup}>
                    <label>Store *</label>
                    <select name="store_id" value={form.store_id} onChange={handleFormChange}>
                      <option value="">Select a store</option>
                      {stores.map((store) => (
                        <option key={store.store_id} value={store.store_id}>
                          {store.store_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              {effectiveUserType === "A" && (
                <>
                  <h3 style={{ margin: "16px 0 8px" }}>Create Store (optional)</h3>
                  <div className={style.formRow}>
                    <div className={style.formGroup}>
                      <label>Store Name</label>
                      <input name="new_store_name" value={form.new_store_name} onChange={handleFormChange} />
                    </div>
                    <div className={style.formGroup}>
                      <label>Store Description</label>
                      <input name="new_store_description" value={form.new_store_description} onChange={handleFormChange} />
                    </div>
                    <div className={style.formGroup}>
                      <label>City</label>
                      <input name="new_store_city" value={form.new_store_city} onChange={handleFormChange} />
                    </div>
                    <div className={style.formGroup}>
                      <label>Store Image</label>
                      <input type="file" accept="image/*" onChange={handleStoreImageChange} />
                    </div>
                  </div>
                </>
              )}
              <h3 style={{ margin: "16px 0 8px" }}>Address</h3>
              <div className={style.formRow}>
                <div className={style.formGroup}>
                  <label>Address Name</label>
                  <input name="address_name" value={form.address_name} onChange={handleFormChange} placeholder="e.g. Home, Office" />
                </div>
                <div className={style.formGroup}>
                  <label>Address Line 1</label>
                  <input name="address1" value={form.address1} onChange={handleFormChange} />
                </div>
                <div className={style.formGroup}>
                  <label>Address Line 2</label>
                  <input name="address2" value={form.address2} onChange={handleFormChange} />
                </div>
                <div className={style.formGroup}>
                  <label>City</label>
                  <input name="city" value={form.city} onChange={handleFormChange} />
                </div>
                <div className={style.formGroup}>
                  <label>Country</label>
                  <input name="country" value={form.country} onChange={handleFormChange} />
                </div>
              </div>
              {formError && <p style={{ color: "red", margin: "8px 0 0" }}>{formError}</p>}
              <div className={style.modalActions}>
                <button type="button" className={style.cancelButton} onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className={style.saveButton} disabled={isLoading}>
                  {isLoading ? "Creating..." : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserPage;
